import axios from "axios";
import mime from "mime";
import {
  Backdrop,
  Button,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { idFetcher } from "../../utilities/fetcher";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useRef, useState } from "react";
import { useProfile } from "../../providers/profile";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";
import SelfieVideo from "../media/selfie-video";
import { ref } from "yup";

export default function Recorder({
  open,
  setOpen,
  onSave,
  isPrivate,
}: {
  open: boolean;
  setOpen: (state: boolean) => void;
  onSave: (details: any) => void;
  isPrivate: boolean;
}) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const recorderRef = useRef<HTMLDivElement>(null);

  const { getIdTokenClaims } = useAuth0();
  const { account, viewProfile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();

  const onCancelClick = useCallback(() => {
    setUploadProgress(0);
    setOpen(false);
  }, [setOpen]);

  const onSaveAction = useCallback(
    async (blob: Blob) => {
      const uuid = uuidv4();
      const untranscodedKey = encodeURIComponent(
        `${isPrivate ? "private" : "protected"}/${
          isPrivate ? viewProfile?.account?.sub : account?.sub
        }/${uuid}.${(mime as any).getExtension(blob.type)}`
      );
      const upload = await idFetcher(
        `/api/s3/transcode/${untranscodedKey}`,
        getIdTokenClaims
      );
      const transcodedExtension =
        (mime as any).getExtension(blob.type) === "mp4" ? "webm" : "mp4";
      const transcodedKey = encodeURIComponent(
        `${isPrivate ? "private" : "protected"}/${
          isPrivate ? viewProfile?.account?.sub : account?.sub
        }/${uuid}.${transcodedExtension}`
      );

      try {
        await axios.put(upload.signedUrl, blob, {
          headers: { "content-type": blob.type },
          onUploadProgress: (evt) => {
            if (!evt.total) {
              setUploadProgress(0);
              return;
            }

            setSubmitting(true);
            setUploadProgress((evt.loaded / evt.total) * 100);
          },
        });

        // FIXME: We need to wait a second while the lambda copies the untranscoded file to the destination bucket
        // To fix this we'll need to make the lambda actually update the urls property of the tutorial or response video
        await new Promise((resolve) => setTimeout(resolve, 1000));

        onSave({ urls: [untranscodedKey, transcodedKey] });
        setOpen(false);
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Problem with file upload. Please try again.", {
          variant: "error",
        });
      } finally {
        setSubmitting(false);
        setUploadProgress(0);
      }
    },
    [
      getIdTokenClaims,
      account,
      viewProfile,
      enqueueSnackbar,
      isPrivate,
      onSave,
      setOpen,
    ]
  );

  return (
    <Backdrop
      sx={{
        alignItems: "flex-start",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        paddingTop: "4rem",
        paddingBottom: "4rem",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        overflow: "auto",
      }}
      open={open}
    >
      <div
        className="flex flex-col gap-4 bg-white rounded-md p-8"
        style={{ width: "100%", maxWidth: "800px" }}
      >
        <div className="flex-1">
          <SelfieVideo active={open} onSave={(b) => onSaveAction(b)} />
        </div>
        <div className="flex gap-4 justify-start">
          <Button
            onClick={onCancelClick}
            color="secondary"
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </div>
    </Backdrop>
  );
}
